import { useIntl } from 'react-intl';

import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { checkFeatureFlagGate } from '@/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@/utils/feature-flags/feature-flag-manifest';

import {
	StaggeredFeatureList,
	featureDataProps,
} from '../../../../../../../../patterns/staggered-feature-list/staggered-feature-list';

import {
	getStaggeredDoubleFeautureFirstImage,
	getStaggeredDoubleFeautureSecondImage,
} from './components/assets/localized-images';

const BuildAndSupportDoubleFeatureSection = () => {
	const intl = useIntl();

	const useGWP7617Gate = checkFeatureFlagGate(featureFlags.gwp7617JiraShortTermAiInfusionGate);

	const tailoredHeadingText = useGWP7617Gate
		? intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.header-text-gwp-7617',
				defaultMessage: 'Tailor for your team',
			})
		: intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.second-feature-header-text',
				defaultMessage: 'Tailor it for your team',
			});

	const tailoredBodyText = useGWP7617Gate
		? intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.second-feature-body-text-gwp-7617',
				defaultMessage:
					'Customize Jira to match your team’s processes, workflows, language, and more. Integrate with every tool you use to get work done. ',
			})
		: intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.second-feature-body-text',
				defaultMessage:
					'Configure Jira to match your team’s processes, workflows, language, and more. Integrate with every tool you use to get work done.',
			});

	const tailoredLinkNew = useLocalizedHref(
		'/software/jira/guides/getting-started/basics#step-3-set-up-your-columns',
	);
	const tailoredLinkOld = useLocalizedHref(
		'/software/jira/guides/issues/overview#what-is-an-issue',
	);

	const tailoredLink = useGWP7617Gate ? tailoredLinkNew : tailoredLinkOld;

	const featureData: featureDataProps[] = [
		{
			headingText: intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.first-feature-header-text',
				defaultMessage: 'Everything in one place',
			}),
			bodyText: intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.first-feature-body-text',
				defaultMessage:
					'The context you need, when you need it. See software team release dates, real-time views of Figma designs, and more, all inside Jira.',
			}),
			linkText: intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.first-feature-link-text',
				defaultMessage: 'Explore features',
			}),
			linkUrl: useLocalizedHref('/software/jira/features'),
			imageLink: getStaggeredDoubleFeautureFirstImage(intl.locale),
			analyticsEventLabel: 'atlas-55799-jira-features-button-clicked',
			imageHeight: 440,
		},
		{
			headingText: tailoredHeadingText,
			bodyText: tailoredBodyText,
			linkText: intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.second-feature-link-text',
				defaultMessage: 'Learn more',
			}),
			linkUrl: tailoredLink,
			imageLink: getStaggeredDoubleFeautureSecondImage(intl.locale),
			analyticsEventLabel: 'atlas-55799-jira-issues-button-clicked',
			imageHeight: 490,
		},
	];

	return (
		<StaggeredFeatureList
			mainHeadingText={intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.header-text',
				defaultMessage: 'Bring every team together under one roof',
			})}
			subHeadingText={intl.formatMessage({
				id: 'jira-product-tour.staggered-double-feature-section.subheader-text',
				defaultMessage:
					'Spend less time trying to get aligned and more time driving projects forward with confidence.',
			})}
			featureData={featureData}
			startingImagePosition="left"
		/>
	);
};
BuildAndSupportDoubleFeatureSection.displayName = 'BuildAndSupportDoubleFeatureSection';

export default BuildAndSupportDoubleFeatureSection;
